import React from 'react';
import { PurchasingStyles } from './styles';
import { CANVAS, DIVIDER, MUTED, RED, TEXT } from './tokens';

// Phone bars sit above the host tab bar (z 30), below its drawer and dropdowns.
export const TABBAR_OFFSET = 'calc(var(--finex-tabbar-h, 0px) + env(safe-area-inset-bottom, 0px))';
/** Room to leave under the last card on phone so the fixed bar never covers it. */
export const PHONE_BAR_SPACE = 140;

export interface SaveSummary {
  /** Bold lead, e.g. "3 unsaved changes" or "Receiving 4 lines". */
  lead: string;
  /** Detail after the lead, joined with " · ". */
  detail?: string[];
  /** Red, e.g. "2 lines need attention". */
  attention?: string | null;
  /** Nothing to save: the lead reads in the muted colour. */
  quiet?: boolean;
}

const Summary: React.FC<{ summary: SaveSummary; phone: boolean }> = ({ summary, phone }) => {
  const detail = (summary.detail ?? []).filter(Boolean);
  if (phone) {
    // Two lines at most: the lead, then the attention note or the detail.
    const second = summary.attention || detail.join(' · ');
    return (
      <p aria-live="polite" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ fontWeight: summary.quiet ? 400 : 600, color: summary.quiet ? MUTED : TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary.lead}</span>
        {second && (
          <>
            <span className="pu-sr"> · </span>
            <span style={{ fontWeight: summary.attention ? 500 : 400, color: summary.attention ? RED : MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{second}</span>
          </>
        )}
      </p>
    );
  }
  return (
    <p aria-live="polite" style={{ flex: '1 1 320px', minWidth: 0, fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ fontWeight: summary.quiet ? 400 : 600, color: summary.quiet ? MUTED : TEXT }}>{summary.lead}</span>
      {detail.map((part) => (
        <span key={part}>{` · ${part}`}</span>
      ))}
      {summary.attention && <span style={{ fontWeight: 500, color: RED }}>{` · ${summary.attention}`}</span>}
    </p>
  );
};

/** Desktop: sticky under the page content. Phone: fixed above the tab bar; `primary` goes full width under the summary. */
export const SaveBar: React.FC<{
  phone: boolean;
  /** Landmark name, e.g. "Unsaved changes". */
  label: string;
  summary: SaveSummary;
  /** Text and secondary actions (desktop: before the primary; phone: on the summary row). */
  secondary?: React.ReactNode;
  primary?: React.ReactNode;
}> = ({ phone, label, summary, secondary, primary }) =>
  phone ? (
    <div
      role="region"
      aria-label={label}
      className="pu-no-print"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: TABBAR_OFFSET,
        zIndex: 35,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px 16px',
        background: '#FFFFFF',
        borderTop: `1px solid ${DIVIDER}`,
        boxShadow: '0 -12px 40px rgba(5,41,42,0.12)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Summary summary={summary} phone />
        {secondary && <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>{secondary}</div>}
      </div>
      {primary}
    </div>
  ) : (
    <div
      role="region"
      aria-label={label}
      className="pu-no-print"
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        flex: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px 16px',
        // Bleeds over the page's 24px padding and sits on its bottom edge (PageFrame).
        margin: 'auto -24px -24px',
        padding: '12px 24px',
        background: '#FFFFFF',
        borderTop: `1px solid ${DIVIDER}`,
        boxShadow: '0 -12px 40px rgba(5,41,42,0.12)',
      }}
    >
      <Summary summary={summary} phone={false} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {secondary}
        {primary}
      </div>
    </div>
  );

/**
 * The page: the canvas colour over the host's content padding (16 phone / 24 desktop), its own scroll,
 * sections 20 / 24 apart, and room under the last card for the phone save bar.
 */
export const PageFrame: React.FC<{ phone: boolean; withPhoneBar?: boolean; children: React.ReactNode }> = ({ phone, withPhoneBar, children }) => {
  const pad = phone ? 16 : 24;
  return (
    <div
      className="pu-scope pu-frame"
      style={{
        margin: `-${pad}px`,
        height: `calc(100% + ${pad * 2}px)`,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: phone ? '20px' : '24px',
        padding: phone ? `${pad}px ${pad}px ${withPhoneBar ? PHONE_BAR_SPACE : pad}px` : `${pad}px`,
        background: CANVAS,
      }}
    >
      <PurchasingStyles />
      {children}
    </div>
  );
};
